import Breadcrumbs from "@mui/material/Breadcrumbs";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { ChevronRightIcon } from "../../shared/components/icons";
import {
  BULK_MOVE_MAX_ROWS,
  childrenOf,
  pathOf,
  sortBySoonestDeadline,
  type Container,
  type DeadlineFilter,
} from "@egalregal/shared";
import { useNavigate } from "react-router-dom";
import { useT } from "../../shared/i18n";
import {
  CONTAINER_DEADLINE_VOCABULARY,
  DeadlineColumn,
  DeadlineColumnHeader,
  DeadlineStatusDot,
  filterByDeadline,
} from "../../shared/components/Deadlines";
import { ContainerMetaChips } from "../../shared/components/ContainerMetaChips";

interface TreeBrowserProps {
  containers: Container[];
  currentId: string | null;
  /** When provided, clicking a container calls onSelect instead of navigating (picker mode). */
  onSelect?: (id: string) => void;
  /** Prefix for per-row data-testids (`<prefix>-<name>`); used by the move parent picker. */
  testIdPrefix?: string;
  /**
   * Render the in-component crumb trail. The Browse view turns it off: the
   * shell's content header owns back, title and crumbs (spec §3). The picker
   * dialogs keep it because no content header renders inside a dialog.
   */
  showBreadcrumbs?: boolean;
  /**
   * The level's control line: `Containers · N` on the left, these actions on
   * the right (container-view 01). Only the Browse view passes them; pickers
   * render the bare level.
   */
  headerActions?: ReactNode;
  /** Opt-in deadline view (deadlines 03): trailing column + sortable header. */
  deadlineView?: boolean;
  deadlineSort?: "alpha" | "soonest";
  onToggleDeadlineSort?: () => void;
  /** Client-side Overdue / Expiring-soon filter (deadlines 04). */
  deadlineFilters?: readonly DeadlineFilter[];
  /** Hidden-state urgency dot; the move/link picker opts out (deadlines 03). */
  showUrgency?: boolean;
  /**
   * Container bulk mode (bulk-move 05): rows toggle selection instead of
   * navigating, checkboxes become visible and a select-all row appears above
   * the list. Selection state stays with the enclosing view.
   */
  bulkMode?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
  /** Select-all receives the visible rows in order; the view applies the cap. */
  onSelectAll?: (ids: string[]) => void;
}

/**
 * File-browser drill-down list with a breadcrumb (ADR-0001): one level at a
 * time, folder rows descend, the breadcrumb walks back. Shared by the Browse
 * view and the link/move picker (ticket 06). Every browse row drills into the
 * merged container page at `/browse/:id` — there is no leaf/detail split any
 * more (container-view 01). The Browse view leaves the breadcrumb to the
 * shell's content header; the deadline view is browse-only, the picker never
 * enables it.
 */
export function TreeBrowser({ containers, currentId, onSelect, testIdPrefix, showBreadcrumbs = true, headerActions, deadlineView = false, deadlineSort = "alpha", onToggleDeadlineSort, deadlineFilters = [], showUrgency = true, bulkMode = false, selectedIds, onToggleSelect, onSelectAll }: TreeBrowserProps) {
  const t = useT();
  const navigate = useNavigate();
  // The trail lives only in the picker dialogs now; there the root is the
  // top level of the visible catalog. Browse leaves title and crumbs to the
  // shell's content header (shell-redesign 10).
  const rootLabel = t("topLevel");
  const crumbs =
    currentId === null
      ? []
      : [{ name: rootLabel, id: null as string | null }, ...pathOf(containers, currentId)];
  const children = childrenOf(containers, currentId);
  // Threshold filter before sorting: colouring stays a property of each row.
  const filtered = filterByDeadline(children, deadlineFilters, CONTAINER_DEADLINE_VOCABULARY);
  // The deadline view sorts: soonest first, dateless last, or name order. Off
  // leaves the stored tree order untouched.
  const rows = deadlineView ? sortedRows(filtered, deadlineSort) : filtered;

  // Every browse row trails a chevron because every row drills into the merged
  // page; in a picker only rows with children can be opened further.
  const leafIds = new Set(rows.map((c) => c.id).filter((id) => childrenOf(containers, id).length === 0));
  const showsChevron = (id: string) => onSelect === undefined || !leafIds.has(id);

  const crumb = (label: string, id: string | null, index: number) => (
    <Typography
      key={`${label}-${index}`}
      component="button"
      onClick={() => navigate(`/browse${id ? `/${id}` : ""}`)}
      sx={{ background: "none", border: "none", cursor: "pointer", p: 0, m: 0, textTransform: "none", font: "inherit", color: "inherit", display: "inline-flex", alignItems: "center", minHeight: 44 }}
    >
      {label}
    </Typography>
  );

  return (
    <>
      {showBreadcrumbs && (
        <Breadcrumbs sx={{ mb: 1 }} aria-label={t("breadcrumb")}>
          {crumbs.length === 0
            ? <Typography color="text.primary">{rootLabel}</Typography>
            : crumbs.map((c, i) =>
                i < crumbs.length - 1
                  ? crumb(c.name, c.id, i)
                  : <Typography key={c.name} color="text.primary">{c.name}</Typography>,
              )}
        </Breadcrumbs>
      )}

      {headerActions && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} data-testid="containers-row">
          <Typography variant="overline" color="text.secondary" sx={{ flexGrow: 1 }}>
            {t("containersTitle")} · {rows.length}
          </Typography>
          {headerActions}
        </Stack>
      )}

      {bulkMode && rows.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Checkbox
            size="small"
            checked={rows.every((c) => selectedIds?.has(c.id))}
            indeterminate={rows.some((c) => selectedIds?.has(c.id)) && !rows.every((c) => selectedIds?.has(c.id))}
            onChange={() => onSelectAll?.(rows.map((c) => c.id))}
            inputProps={{ "aria-label": t("bulkSelectAll") }}
            data-testid="bulk-select-all"
          />
          <Typography variant="body2" color="text.secondary">
            {t("bulkSelectAll")}
          </Typography>
          {rows.length > BULK_MOVE_MAX_ROWS && (
            <Chip
              size="small"
              variant="outlined"
              label={t("bulkCapHint", { count: String(BULK_MOVE_MAX_ROWS) })}
            />
          )}
        </Stack>
      )}

      {children.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("hereEmpty")}
        </Typography>
      ) : filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("deadlineFilterEmpty")}
        </Typography>
      ) : (
        <List aria-label={t("containersTitle")}>
          {deadlineView && onToggleDeadlineSort && (
            <DeadlineColumnHeader
              label={t("containersTitle")}
              soonest={deadlineSort === "soonest"}
              onToggleSort={onToggleDeadlineSort}
            />
          )}
          {rows.map((c) => (
            <ListItem key={c.id} disablePadding>
              <ListItemButton
                data-testid={testIdPrefix ? `${testIdPrefix}-${c.name}` : undefined}
                selected={bulkMode ? selectedIds?.has(c.id) : undefined}
                onClick={() => {
                  if (bulkMode) {
                    onToggleSelect?.(c.id);
                  } else if (onSelect) {
                    onSelect(c.id);
                  } else {
                    navigate(`/browse/${c.id}`);
                  }
                }}
              >
                {bulkMode && (
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      edge="start"
                      checked={selectedIds?.has(c.id) ?? false}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-label": c.name }}
                      data-testid={`bulk-check-${c.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleSelect?.(c.id);
                      }}
                    />
                  </ListItemIcon>
                )}
                <ListItemText primary={c.name} secondary={c.description || undefined} />
                {!deadlineView && showUrgency && (
                  <DeadlineStatusDot
                    properties={c.properties}
                    vocabulary={CONTAINER_DEADLINE_VOCABULARY}
                  />
                )}
                <ContainerMetaChips
                  owner={c.owner}
                  kind={c.kind}
                  sx={{ ml: 1 }}
                  ownerTestId={`row-owner-${c.id}`}
                />
                {deadlineView && (
                  <Box sx={{ width: { xs: 112, sm: 168 }, flexShrink: 0, ml: 1 }}>
                    <DeadlineColumn
                      properties={c.properties}
                      vocabulary={CONTAINER_DEADLINE_VOCABULARY}
                    />
                  </Box>
                )}
                {showsChevron(c.id) && <ChevronRightIcon color="action" />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );
}

/** Rows in the deadline view: soonest first (dateless last) or name order. */
function sortedRows(rows: Container[], sort: "alpha" | "soonest"): Container[] {
  if (sort === "soonest") return sortBySoonestDeadline(rows, CONTAINER_DEADLINE_VOCABULARY);
  return [...rows].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}